import { useRef, useState, type KeyboardEvent } from "react";
import { trackEvent } from "../lib/analytics";

/* Static, hand-written example briefing shown near the top of the homepage.
   No AI or backend calls — clearly labelled as an example. Mirrors the dark
   app palette used in AppDemo so it matches the in-app look from the ads. */

const D = {
  bg: "#080b14",
  card: "#0e1225",
  surf: "#131829",
  b: "#1e2540",
  a: "#818cf8",
  ab: "#a5b4fc",
  t0: "#f1f5f9",
  t1: "#c3cee0",
  t2: "#8f9db5",
};

interface Source {
  name: string;
  title: string;
  url: string;
}

interface Topic {
  id: string;
  label: string;
  date: string;
  story: { headline: string; bullets: string[]; source: Source };
  next: { headline: string; firstBullet: string };
}

/* Real, dated historical announcements. Every fact below is taken from the
   exact linked page (verified when written). No current-week claims. */
const TOPICS: Topic[] = [
  {
    id: "ai",
    label: "AI",
    date: "May 13, 2024",
    story: {
      headline: "OpenAI announces GPT-4o",
      bullets: [
        "A single model that works across text, audio, and images.",
        "Responds to audio in as little as 232 ms, about 320 ms on average.",
        "Rolling out in ChatGPT, including the free tier.",
      ],
      source: { name: "OpenAI", title: "Hello GPT-4o", url: "https://openai.com/index/hello-gpt-4o/" },
    },
    next: {
      headline: "What it means for developers",
      firstBullet: "In the API, GPT-4o is 2x faster and half the price of GPT-4 Turbo.",
    },
  },
  {
    id: "markets",
    label: "Markets",
    date: "Sep 18, 2024",
    story: {
      headline: "Fed cuts rates by half a point",
      bullets: [
        "Target range lowered to 4-3/4 to 5 percent.",
        "The Committee cited progress on inflation and the balance of risks.",
        "Future moves depend on incoming data and the outlook.",
      ],
      source: {
        name: "Federal Reserve",
        title: "FOMC statement, Sep 18, 2024",
        url: "https://www.federalreserve.gov/newsevents/pressreleases/monetary20240918a.htm",
      },
    },
    next: {
      headline: "Balance sheet",
      firstBullet: "The Fed will keep reducing its Treasury and mortgage-backed holdings.",
    },
  },
  {
    id: "nfl",
    label: "NFL",
    date: "Mar 26, 2024",
    story: {
      headline: "Owners approve the new hybrid kickoff",
      bullets: [
        "The vote passed 29-3 at the Annual League Meeting.",
        "Kicking team lines up at the opponent's 40, aiming for more returns.",
        "Approved for the 2024 season only, then up for renewal.",
      ],
      source: {
        name: "NFL.com",
        title: "NFL owners pass new hybrid kickoff rule",
        url: "https://www.nfl.com/news/nfl-owners-pass-new-kickoff-rule-at-annual-league-meeting",
      },
    },
    next: {
      headline: "Onside kicks change too",
      firstBullet: "Only allowed in the fourth quarter by a trailing team, and must be declared.",
    },
  },
];

export default function SampleBriefing() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const t = TOPICS[active];

  function selectTopic(index: number) {
    if (index === active) return;
    setActive(index);
    // Only the fixed example ID is sent, never a visitor's custom topic.
    trackEvent("example_topic_selected", { topic: TOPICS[index].id });
  }

  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    let n = active;
    if (e.key === "ArrowRight") n = (active + 1) % TOPICS.length;
    else if (e.key === "ArrowLeft") n = (active - 1 + TOPICS.length) % TOPICS.length;
    else if (e.key === "Home") n = 0;
    else if (e.key === "End") n = TOPICS.length - 1;
    else return;
    e.preventDefault();
    selectTopic(n);
    tabRefs.current[n]?.focus();
  };

  return (
    <figure className="relative w-full max-w-md mx-auto lg:mx-0 m-0" aria-label="Example Meriana briefing">
      <div
        aria-hidden
        className="absolute -inset-4 rounded-[2rem] opacity-60 blur-2xl pointer-events-none"
        style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(99,102,241,0.28), transparent 70%)" }}
      />
      <div
        className="relative rounded-2xl overflow-hidden shadow-2xl text-left"
        style={{ background: D.bg, border: "1px solid " + D.b }}
      >
        {/* header */}
        <div className="px-5 pt-4 pb-3" style={{ background: D.card, borderBottom: "1px solid " + D.b }}>
          <div className="flex items-center justify-between gap-3">
            <span
              className="font-sans"
              style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: D.t2, textTransform: "uppercase" }}
            >
              Your briefing
            </span>
            <span
              className="font-sans rounded-full px-2.5 py-0.5"
              style={{ fontSize: 10.5, fontWeight: 700, color: D.ab, border: "1px solid #818cf855", background: "#818cf81a" }}
            >
              Example · past news
            </span>
          </div>
          <div role="tablist" aria-label="Example topics" className="flex gap-1.5 mt-3">
            {TOPICS.map((tp, i) => {
              const on = i === active;
              return (
                <button
                  key={tp.id}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  role="tab"
                  id={`sample-tab-${tp.id}`}
                  aria-selected={on}
                  aria-controls="sample-panel"
                  tabIndex={on ? 0 : -1}
                  onClick={() => selectTopic(i)}
                  onKeyDown={onKey}
                  data-testid={`tab-sample-${tp.id}`}
                  className="font-sans rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-300"
                  style={{
                    border: "1px solid " + (on ? "#818cf866" : D.b),
                    background: on ? "#818cf826" : "transparent",
                    color: on ? D.ab : D.t1,
                  }}
                >
                  {tp.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* body */}
        <div
          id="sample-panel"
          role="tabpanel"
          aria-labelledby={`sample-tab-${t.id}`}
          className="px-5 pt-5 pb-4 font-sans"
        >
          <article>
            <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", color: D.a, textTransform: "uppercase", margin: "0 0 6px" }}>
              {t.label} · <time>{t.date}</time>
            </p>
            <h3 className="font-display m-0" style={{ fontSize: 20, color: D.t0, lineHeight: 1.25 }}>
              {t.story.headline}
            </h3>
            <ul className="m-0 mt-3 p-0 list-none flex flex-col gap-2">
              {t.story.bullets.map((bl) => (
                <li key={bl} className="flex gap-2.5" style={{ fontSize: 15, color: D.t1, lineHeight: 1.5 }}>
                  <span aria-hidden style={{ marginTop: 9, width: 5, height: 5, borderRadius: 9, background: D.a, flexShrink: 0 }} />
                  <span>{bl}</span>
                </li>
              ))}
            </ul>
            <p style={{ fontSize: 12.5, color: D.t2, margin: "12px 0 0", lineHeight: 1.5 }}>
              Source:{" "}
              <a
                href={t.story.source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-indigo-200"
                style={{ color: D.ab, textDecoration: "underline", textUnderlineOffset: 3 }}
              >
                {t.story.source.name}, “{t.story.source.title}”
              </a>
            </p>
          </article>

          <div className="relative" style={{ marginTop: 16, paddingTop: 14, borderTop: "1px dashed " + D.b }}>
            <p style={{ fontSize: 16, fontWeight: 700, color: D.t0, lineHeight: 1.35, margin: 0 }}>{t.next.headline}</p>
            <p style={{ fontSize: 15, color: D.t1, lineHeight: 1.5, margin: "6px 0 0" }}>{t.next.firstBullet}</p>
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-8 pointer-events-none"
              style={{ background: `linear-gradient(to bottom, transparent, ${D.bg})` }}
            />
          </div>
        </div>

        <div className="px-5 py-3 font-sans" style={{ background: D.card, borderTop: "1px solid " + D.b, fontSize: 11.5, color: D.t2 }}>
          Static example built from dated, linked announcements. Not today's news. Your briefings cover the topics you choose.
        </div>
      </div>
    </figure>
  );
}
